import { StrictMode, useEffect, useSyncExternalStore, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';

import { AUTH_PATHS, REGISTER_PATH, VERIFY_EMAIL_PATH, readInviteCode } from './lib/routes';
import { AppPage } from './pages/AppPage';
import { InvitePage } from './pages/InvitePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { VerifyPendingPage } from './pages/VerifyPendingPage';
import { AuthProvider, useAuth } from './store/auth';

/**
 * Entry point and router.
 *
 * The app has few enough screens that the browser history is enough of a
 * router: the path selects the screen and `popstate` re-renders. Whether the
 * visitor is authenticated is decided by the auth store, never by the URL — a
 * path is a request for a screen, not permission to see it.
 *
 * `/verify-email` is the one path that outranks the session state, in both
 * directions: it is reached from a mail client that carries no session, and a
 * signed-in visitor who opens it wants the confirmation, not the app.
 *
 * `/invite/<code>` is how somebody who is already signed up enters another
 * server. A visitor with no session gets the login screen while the address
 * stays put, so signing in brings the invite back on its own — there is
 * nothing to remember and nothing to lose.
 */

function subscribeToPath(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  return () => window.removeEventListener('popstate', onChange);
}

function readPath(): string {
  return window.location.pathname;
}

function usePath(): string {
  return useSyncExternalStore(subscribeToPath, readPath, readPath);
}

function Router(): ReactElement {
  const { user, loading } = useAuth();
  const path = usePath();
  const inviteCode = readInviteCode(path);

  useEffect(() => {
    // Once signed in the auth screens have nothing left to show, and leaving
    // `/register` in the address bar would resend the invite link on reload.
    if (user && AUTH_PATHS.has(path)) {
      window.history.replaceState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [user, path]);

  if (loading) {
    return (
      <main className="auth-screen">
        <p className="auth-subtitle">Carregando…</p>
      </main>
    );
  }

  if (path === VERIFY_EMAIL_PATH) {
    return <VerifyEmailPage />;
  }

  if (!user) {
    if (path === REGISTER_PATH) {
      return <RegisterPage />;
    }

    // The invite code travels into the login screen only to offer the sign-up
    // link: whoever has no account yet cannot sign in, and the code is what
    // opens the registration.
    return inviteCode === null ? <LoginPage /> : <LoginPage inviteCode={inviteCode} />;
  }

  // Before the confirmation screen on purpose: entering a server is allowed
  // while the address is pending, and the confirmation is what comes next
  // anyway — the app itself is what waits for it.
  if (inviteCode !== null) {
    return <InvitePage code={inviteCode} />;
  }

  // Signed in, but the API will refuse every channel with `email_not_verified`
  // until the address is confirmed — so the confirmation screen is the app.
  if (!user.emailVerified) {
    return <VerifyPendingPage />;
  }

  return <AppPage />;
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Elemento #root não encontrado: verifique o index.html.');
}

createRoot(container).render(
  <StrictMode>
    <AuthProvider>
      <Router />
    </AuthProvider>
  </StrictMode>,
);
